import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import './Profile.css';
import CreateEventPanel from './CreateEventPanel';
import CreateCommunityEventPanel from './CreateCommunityEventPanel';
import CreatePublicActivityPanel from './CreatePublicActivityPanel';
import UserAvatar from './UserAvatar';
import { usersAPI, badgesAPI, eventsAPI, picturesAPI } from './services/api';
import { AvatarType } from './constants/avatarTypes';
import { parseUTCDate } from './utils/dateUtils';
import PictureUploadModal from './PictureUploadModal';
import { buildGroupPhotoExistsMap } from './utils/pictureUtils';
import ReactDialog from './ReactDialog';
import SendStarDialog from './SendStarDialog';
import UserDetail from './UserDetail';

// Helper function to determine event type from eventType field
const getEventType = (eventType) => {
  switch (eventType) {
    case 1: return 'community_work';
    case 2:
    case 3: return 'public_activity_art';
    case 4: return 'public_activity_sport';
    case 6: return 'public_activity_education';
    default: return 'personal';
  }
};

function Profile({ currentUser: currentUserProp, onLogout, onUserUpdate }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(currentUserProp);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hashtags, setHashtags] = useState([]);
  const [loadingHashtags, setLoadingHashtags] = useState(true);
  const [giveawayStars, setGiveawayStars] = useState(0);
  const [loadingStars, setLoadingStars] = useState(true);
  const [pastGatherings, setPastGatherings] = useState([]);
  const [loadingPastGatherings, setLoadingPastGatherings] = useState(true);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showCreateCommunityEvent, setShowCreateCommunityEvent] = useState(false);
  const [showCreatePublicActivity, setShowCreatePublicActivity] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [existingPictures, setExistingPictures] = useState({});
  const [groupPhotoEventId, setGroupPhotoEventId] = useState(null);
  const [groupPhotoExists, setGroupPhotoExists] = useState({});
  const [groupPhotoEvent, setGroupPhotoEvent] = useState(null);
  const [userPictures, setUserPictures] = useState([]);
  const [loadingPictures, setLoadingPictures] = useState(true);
  const [selectedPictureIndex, setSelectedPictureIndex] = useState(null);
  const [slideshowIndex] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showStarModal, setShowStarModal] = useState(false);
  const [selectedStarRecipient, setSelectedStarRecipient] = useState(null);
  const [givenStars, setGivenStars] = useState({});
  const [savingAvatarType, setSavingAvatarType] = useState(false);
  const [lastMonthCount, setLastMonthCount] = useState(0);
  const [descriptionPopup, setDescriptionPopup] = useState(null);

  // Avatar preference modal state
  const [showAvatarPreferenceModal, setShowAvatarPreferenceModal] = useState(false);

  // Avatar picture upload state
  const [showAvatarUploadModal, setShowAvatarUploadModal] = useState(false);
  const [avatarPictureFile, setAvatarPictureFile] = useState(null);
  const [uploadingAvatarPicture, setUploadingAvatarPicture] = useState(false);

  // Avatar picture selection state
  const [showPictureSelectionModal, setShowPictureSelectionModal] = useState(false);

  // Public activity counts
  const [publicActivityArtCount, setPublicActivityArtCount] = useState(0);
  const [publicActivitySportCount, setPublicActivitySportCount] = useState(0);
  const [publicActivityEducationCount, setPublicActivityEducationCount] = useState(0);
  const [publicActivityArtLastMonth, setPublicActivityArtLastMonth] = useState(0);
  const [publicActivitySportLastMonth, setPublicActivitySportLastMonth] = useState(0);
  const [publicActivityEducationLastMonth, setPublicActivityEducationLastMonth] = useState(0);

  // Received badges state
  const [receivedBadges, setReceivedBadges] = useState([]);
  const [loadingBadges, setLoadingBadges] = useState(true);
  const [showBadgeReactionModal, setShowBadgeReactionModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [settingReaction, setSettingReaction] = useState(false);
  const [badgeOffended, setBadgeOffended] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState(null);
  const [unreactedBadgeCount, setUnreactedBadgeCount] = useState(0);

  // Sent badges state
  const [sentBadges, setSentBadges] = useState([]);
  const [loadingSentBadges, setLoadingSentBadges] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState('profile');

  // Event companions state
  const [eventCompanions, setEventCompanions] = useState([]);
  const [loadingEventCompanions, setLoadingEventCompanions] = useState(true);

  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Resend verification email state
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Inline field edit state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingField, setSavingField] = useState(false);
  const [locationAddress, setLocationAddress] = useState('');
  const [homeCountry, setHomeCountry] = useState('');

  // Handle navigation to specific tab via location state
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Clear the state after using it to prevent it from persisting
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Refresh currentUser data from API on component mount
  useEffect(() => {
    const refreshCurrentUser = async () => {
      if (currentUserProp) {
        try {
          const refreshedUser = await usersAPI.getById(currentUserProp.id);
          setCurrentUser(refreshedUser);
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(refreshedUser));
        } catch (error) {
          console.error('Error refreshing user data:', error);
          setCurrentUser(currentUserProp);
        }
      }
    };
    refreshCurrentUser();
  }, [currentUserProp]);

  // Region preferences state
  const [homeLatitude, setHomeLatitude] = useState(currentUser?.homeLatitude || null);
  const [homeLongitude, setHomeLongitude] = useState(currentUser?.homeLongitude || null);
  const [preferredRadius, setPreferredRadius] = useState(currentUser?.preferredRadiusKm || 10);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Update state when currentUser changes (e.g., after save or page reload)
  useEffect(() => {
    if (currentUser?.homeLatitude && currentUser?.homeLongitude) {
      setHomeLatitude(currentUser.homeLatitude);
      setHomeLongitude(currentUser.homeLongitude);
      setPreferredRadius(currentUser.preferredRadiusKm || 10);

      // Update marker position if map is already initialized
      if (markerRef.current && mapInstanceRef.current) {
        const newPosition = { lat: currentUser.homeLatitude, lng: currentUser.homeLongitude };
        markerRef.current.setPosition(newPosition);
        mapInstanceRef.current.setCenter(newPosition);
      }
    }
  }, [currentUser?.homeLatitude, currentUser?.homeLongitude, currentUser?.preferredRadiusKm]);

  useEffect(() => {
    // Load all users from API for friend selection
    const loadUsers = async () => {
      try {
        setLoading(true);
        const users = await usersAPI.getAll();
        setAllUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's hashtags
    const loadHashtags = async () => {
      try {
        setLoadingHashtags(true);
        const userHashtags = await usersAPI.getHashtags(currentUser.id);
        setHashtags(userHashtags);
      } catch (error) {
        console.error('Error loading hashtags:', error);
      } finally {
        setLoadingHashtags(false);
      }
    };

    loadHashtags();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's giveaway stars count
    const loadGiveawayStars = async () => {
      try {
        setLoadingStars(true);
        const count = await badgesAPI.getGiveawayCount(currentUser.id);
        setGiveawayStars(count);
      } catch (error) {
        console.error('Error loading giveaway stars:', error);
      } finally {
        setLoadingStars(false);
      }
    };

    loadGiveawayStars();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's past gatherings
    const loadPastGatherings = async () => {
      try {
        setLoadingPastGatherings(true);
        const events = await eventsAPI.getUserPastEvents(currentUser.id);
        console.log('Past gatherings loaded:', events.length, 'events');
        console.log('Events:', events);
        setPastGatherings(events);

        // Check if organizer already uploaded a group photo for each event they organize
        const photoMap = await buildGroupPhotoExistsMap(events, currentUser.id);
        setGroupPhotoExists(photoMap);
      } catch (error) {
        console.error('Error loading past gatherings:', error);
      } finally {
        setLoadingPastGatherings(false);
      }
    };

    loadPastGatherings();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's pictures (including unapproved for management)
    const loadPictures = async () => {
      try {
        setLoadingPictures(true);
        const pictures = await picturesAPI.getUserPicturesAll(currentUser.id);
        setUserPictures(pictures);
      } catch (error) {
        console.error('Error loading pictures:', error);
      } finally {
        setLoadingPictures(false);
      }
    };

    loadPictures();
  }, [currentUser]);


  useEffect(() => {
    if (!currentUser) return;

    // Load community work count for last month
    const loadLastMonthCount = async () => {
      try {
        const count = await usersAPI.getCommunityWorkLastMonth(currentUser.id);
        setLastMonthCount(count);
      } catch (error) {
        console.error('Error loading last month count:', error);
      }
    };

    loadLastMonthCount();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load public activity counts
    const loadPublicActivityCounts = async () => {
      try {
        // Get user's past events to count public activities
        const events = await eventsAPI.getUserPastEvents(currentUser.id);

        // EventType values: 2=Art, 3=Music, 4=Sport, 6=Education
        let artCount = 0;
        let sportCount = 0;
        let educationCount = 0;
        let artLastMonth = 0;
        let sportLastMonth = 0;
        let educationLastMonth = 0;

        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

        events.forEach(event => {
          const eventDate = parseUTCDate(event.dateTime);
          const isArt = event.eventType === 2 || event.eventType === 3;
          const isSport = event.eventType === 4;
          const isEducation = event.eventType === 6;

          if (isArt) {
            artCount++;
            if (eventDate >= lastMonth) {
              artLastMonth++;
            }
          }

          if (isSport) {
            sportCount++;
            if (eventDate >= lastMonth) {
              sportLastMonth++;
            }
          }

          if (isEducation) {
            educationCount++;
            if (eventDate >= lastMonth) {
              educationLastMonth++;
            }
          }
        });

        setPublicActivityArtCount(artCount);
        setPublicActivitySportCount(sportCount);
        setPublicActivityEducationCount(educationCount);
        setPublicActivityArtLastMonth(artLastMonth);
        setPublicActivitySportLastMonth(sportLastMonth);
        setPublicActivityEducationLastMonth(educationLastMonth);
      } catch (error) {
        console.error('Error loading public activity counts:', error);
      }
    };

    loadPublicActivityCounts();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's received badges
    const loadReceivedBadges = async () => {
      try {
        setLoadingBadges(true);
        const badges = await badgesAPI.getReceivedBadges(currentUser.id);
        setReceivedBadges(badges);
      } catch (error) {
        console.error('Error loading received badges:', error);
      } finally {
        setLoadingBadges(false);
      }
    };

    // Load unreacted badge count
    const loadUnreactedCount = async () => {
      try {
        const count = await badgesAPI.getUnreactedCount(currentUser.id);
        setUnreactedBadgeCount(count);
      } catch (error) {
        console.error('Error loading unreacted badge count:', error);
      }
    };

    loadReceivedBadges();
    loadUnreactedCount();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load user's sent badges
    const loadSentBadges = async () => {
      try {
        setLoadingSentBadges(true);
        const badges = await badgesAPI.getSentBadges(currentUser.id);
        setSentBadges(badges);
      } catch (error) {
        console.error('Error loading sent badges:', error);
      } finally {
        setLoadingSentBadges(false);
      }
    };

    loadSentBadges();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Load event companions
    const loadEventCompanions = async () => {
      try {
        setLoadingEventCompanions(true);
        const companions = await usersAPI.getEventCompanions(currentUser.id);
        setEventCompanions(companions);
      } catch (error) {
        console.error('Error loading event companions:', error);
      } finally {
        setLoadingEventCompanions(false);
      }
    };

    loadEventCompanions();
  }, [currentUser]);

  const handleBadgeClick = (badge) => {
    setSelectedBadge(badge);
    setShowBadgeReactionModal(true);
  };

  const handleCloseBadgeReactionModal = () => {
    setShowBadgeReactionModal(false);
    setSelectedBadge(null);
    setBadgeOffended(false);
    setSelectedReaction(null);
  };

  const handleSubmitReaction = async () => {
    if (!selectedBadge || !selectedReaction) return;

    try {
      setSettingReaction(true);
      await badgesAPI.setReaction(selectedBadge.id, selectedReaction, badgeOffended);

      // Update the badge in state with the new reaction
      setReceivedBadges(prev =>
        prev.map(b => b.id === selectedBadge.id ? { ...b, reaction: selectedReaction } : b)
      );

      // Refresh unreacted count after setting reaction
      const count = await badgesAPI.getUnreactedCount(currentUser.id);
      setUnreactedBadgeCount(count);

      handleCloseBadgeReactionModal();
    } catch (error) {
      console.error('Error setting reaction:', error);
      const errorMessage = error.response?.data || t('profile.alerts.failedReaction');
      alert(errorMessage);
    } finally {
      setSettingReaction(false);
    }
  };

  const handleShowParticipants = async (event) => {
    let fullEvent = event;
    try {
      fullEvent = await eventsAPI.getById(event.id);
    } catch (error) {
      console.error('Error loading participants:', error);
    }

    setSelectedEvent(fullEvent);

    if (currentUser) {
      const pictureStatus = {};
      const starStatus = {};

      try {
        const organizerExists = await picturesAPI.checkExists(currentUser.id, fullEvent.organizer.id, fullEvent.id);
        pictureStatus[fullEvent.organizer.id] = organizerExists;
        const starGiven = await badgesAPI.checkIfGiven(currentUser.id, fullEvent.organizer.id, fullEvent.id);
        starStatus[fullEvent.organizer.id] = starGiven;
      } catch (error) {
        console.error('Error checking organizer status:', error);
        pictureStatus[fullEvent.organizer.id] = false;
        starStatus[fullEvent.organizer.id] = false;
      }

      if (fullEvent.participants) {
        for (const participant of fullEvent.participants) {
          try {
            const exists = await picturesAPI.checkExists(currentUser.id, participant.user.id, fullEvent.id);
            pictureStatus[participant.user.id] = exists;
            const starGiven = await badgesAPI.checkIfGiven(currentUser.id, participant.user.id, fullEvent.id);
            starStatus[participant.user.id] = starGiven;
          } catch (error) {
            console.error('Error checking participant status:', error);
            pictureStatus[participant.user.id] = false;
            starStatus[participant.user.id] = false;
          }
        }
      }

      setExistingPictures(pictureStatus);
      setGivenStars(starStatus);
    }
  };

  const handleCloseParticipants = () => {
    setSelectedEvent(null);
    setExistingPictures({});
    setGivenStars({});
  };

  const handleAddPicture = (participant) => {
    setSelectedParticipant(participant);
    setGroupPhotoEventId(null);
    setShowUploadModal(true);
  };

  const handleAddGroupPhoto = (event) => {
    setSelectedParticipant(null);
    setGroupPhotoEventId(event.id);
    setGroupPhotoEvent(event);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedParticipant(null);
    setGroupPhotoEventId(null);
    setGroupPhotoEvent(null);
  };

  const handleSendStar = (participant) => {
    if (giveawayStars === 0) {
      alert(t('profile.alerts.noStars'));
      return;
    }
    setSelectedStarRecipient(participant);
    setShowStarModal(true);
  };

  const handleCloseStarModal = () => {
    setShowStarModal(false);
    setSelectedStarRecipient(null);
  };

  const handleSubmitStar = async (message, isAnonymous, isPrivate) => {
    if (!selectedStarRecipient || !selectedEvent) {
      alert(t('profile.alerts.missingInfo'));
      return;
    }

    try {
      await badgesAPI.giveBadge(
        selectedStarRecipient.user.id,
        selectedEvent.id,
        message,
        isAnonymous,
        isPrivate
      );

      alert(isAnonymous
        ? t('profile.alerts.starSentAnonymous', { name: selectedStarRecipient.user.displayName })
        : t('profile.alerts.starSent', { name: selectedStarRecipient.user.displayName }));

      const count = await badgesAPI.getGiveawayCount(currentUser.id);
      setGiveawayStars(count);

      setGivenStars(prev => ({ ...prev, [selectedStarRecipient.user.id]: true }));
      handleCloseStarModal();
    } catch (error) {
      console.error('Error sending star:', error);
      const errorMessage = error.response?.data?.message || error.response?.data || t('profile.alerts.failedStar');
      alert(errorMessage);
    }
  };


  const handleApprovalToggle = async (pictureId, currentlyApproved) => {
    try {
      if (currentlyApproved) {
        await picturesAPI.disapprovePicture(pictureId);
      } else {
        await picturesAPI.approvePicture(pictureId);
      }

      // Update local state immediately for responsive UI
      setUserPictures(prev => prev.map(pic =>
        pic.id === pictureId ? { ...pic, approved: !currentlyApproved } : pic
      ));
    } catch (error) {
      console.error('Error toggling picture approval:', error);
      alert(t('profile.alerts.failedApproval'));
    }
  };

  const handleDeletePicture = async (pictureId) => {
    if (!window.confirm(t('profile.alerts.confirmDelete'))) {
      return;
    }

    try {
      await picturesAPI.softDelete(pictureId);

      // Remove picture from local state
      setUserPictures(prev => prev.filter(pic => pic.id !== pictureId));

      alert(t('profile.alerts.pictureDeleted'));
    } catch (error) {
      console.error('Error deleting picture:', error);
      alert(t('profile.alerts.failedDeletePicture'));
    }
  };

  const handleCloseFullscreen = () => {
    setSelectedPictureIndex(null);
  };

  const handlePreviousPicture = () => {
    if (selectedPictureIndex !== null && selectedPictureIndex > 0) {
      setSelectedPictureIndex(selectedPictureIndex - 1);
    }
  };

  const handleNextPicture = () => {
    if (selectedPictureIndex !== null && selectedPictureIndex < userPictures.length - 1) {
      setSelectedPictureIndex(selectedPictureIndex + 1);
    }
  };


  const reverseGeocode = (lat, lng) => {
    if (!window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setLocationAddress(results[0].formatted_address);
        const countryComponent = results[0].address_components?.find(c => c.types.includes('country'));
        setHomeCountry(countryComponent?.short_name || '');
      } else {
        setLocationAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        setHomeCountry('');
      }
    });
  };

  useEffect(() => {
    if (!currentUser?.homeLatitude || !currentUser?.homeLongitude) return;
    const doGeocode = () => {
      if (window.google?.maps) {
        reverseGeocode(currentUser.homeLatitude, currentUser.homeLongitude);
      } else {
        setTimeout(doGeocode, 500);
      }
    };
    doGeocode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.homeLatitude, currentUser?.homeLongitude]);

  // Initialize Google Maps for location editing - only when location field is being edited
  useEffect(() => {
    if (editingField !== 'location') return;

    // If map is already initialized, just resize it (in case container size changed)
    if (mapInstanceRef.current) {
      window.google?.maps?.event?.trigger(mapInstanceRef.current, 'resize');
      return;
    }

    const initMap = () => {
      if (!mapRef.current) {
        setTimeout(initMap, 100);
        return;
      }

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 100);
        return;
      }

      try {
        const defaultLat = currentUser?.homeLatitude || 42.6977;  // Sofia, Bulgaria
        const defaultLng = currentUser?.homeLongitude || 23.3219;

        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: defaultLat, lng: defaultLng },
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false
        });

        const marker = new window.google.maps.Marker({
          position: { lat: defaultLat, lng: defaultLng },
          map: map,
          draggable: true,
          title: "Drag me to set your home location"
        });

        setHomeLatitude(defaultLat);
        setHomeLongitude(defaultLng);

        marker.addListener('dragend', (event) => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          setHomeLatitude(newLat);
          setHomeLongitude(newLng);
          reverseGeocode(newLat, newLng);
        });

        map.addListener('click', (event) => {
          const clickedLat = event.latLng.lat();
          const clickedLng = event.latLng.lng();
          marker.setPosition(event.latLng);
          setHomeLatitude(clickedLat);
          setHomeLongitude(clickedLng);
          reverseGeocode(clickedLat, clickedLng);
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    setTimeout(initMap, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingField, currentUser]);

  const handleSaveLocation = async () => {
    if (!homeLatitude || !homeLongitude) {
      alert(t('profile.alerts.selectLocation'));
      return;
    }
    try {
      setSavingField(true);
      await usersAPI.updateLocationPreferences(currentUser.id, {
        homeLatitude,
        homeLongitude,
        preferredRadiusKm: preferredRadius,
        country: homeCountry || null
      });
      const updatedUser = await usersAPI.getById(currentUser.id);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      onUserUpdate?.(updatedUser);
      setEditingField(null);
      mapInstanceRef.current = null;
      markerRef.current = null;
    } catch (error) {
      console.error('Error saving location:', error);
      alert(t('profile.alerts.failedSaveLocation'));
    } finally {
      setSavingField(false);
    }
  };

  const handleSaveRadius = async () => {
    try {
      setSavingField(true);
      const newRadius = parseInt(editValue);
      await usersAPI.updateLocationPreferences(currentUser.id, {
        homeLatitude: currentUser.homeLatitude || homeLatitude || 42.6977,
        homeLongitude: currentUser.homeLongitude || homeLongitude || 23.3219,
        preferredRadiusKm: newRadius,
        country: currentUser.country || homeCountry || null
      });
      const updatedUser = { ...currentUser, preferredRadiusKm: newRadius };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      onUserUpdate?.(updatedUser);
      setPreferredRadius(newRadius);
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      console.error('Error saving radius:', error);
      alert(t('profile.alerts.failedSaveRadius'));
    } finally {
      setSavingField(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    try {
      setDeletingAccount(true);
      await usersAPI.gdprDelete(currentUser.id);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(t('profile.alerts.failedDeleteAccount'));
      setDeletingAccount(false);
    }
  };

  const handleAvatarTypeChange = async (newAvatarType) => {
    if (!currentUser) return;

    try {
      setSavingAvatarType(true);
      await usersAPI.updateAvatarType(currentUser.id, newAvatarType);

      // Update currentUser in localStorage and state
      const updatedUser = { ...currentUser, avatarType: newAvatarType };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('userUpdated'));
    } catch (error) {
      console.error('Error updating avatar type:', error);
      alert(t('profile.alerts.failedAvatarType'));
    } finally {
      setSavingAvatarType(false);
    }
  };

  const handleAvatarPictureSelected = async (pictureId) => {
    if (!currentUser) return;

    try {
      const updatedUser = await usersAPI.updateAvatarPicture(currentUser.id, pictureId);

      // Update currentUser in localStorage and state
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);

      // Close the picture selection modal
      setShowPictureSelectionModal(false);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('userUpdated'));
    } catch (error) {
      console.error('Error updating avatar picture:', error);
      alert(t('profile.alerts.failedAvatarPicture'));
    }
  };

  const handleClearAvatarPicture = async () => {
    if (!currentUser) return;

    try {
      const updatedUser = await usersAPI.updateAvatarPicture(currentUser.id, null);

      // Update currentUser in localStorage and state
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);

      // Close the picture selection modal
      setShowPictureSelectionModal(false);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('userUpdated'));
    } catch (error) {
      console.error('Error clearing avatar picture:', error);
      alert(t('profile.alerts.failedClearAvatar'));
    }
  };

  const handleResendVerification = async () => {
    setSendingVerification(true);
    try {
      await usersAPI.resendVerificationEmail(currentUser.id);
      setVerificationSent(true);
    } catch {
      // silently fail
    } finally {
      setSendingVerification(false);
    }
  };

  const handleStartEdit = (field, currentValue) => {
    if (field === 'birthDate' && currentValue) {
      setEditValue(new Date(currentValue).toISOString().split('T')[0]);
    } else {
      setEditValue(currentValue ?? '');
    }
    setEditingField(field);
  };

  const handleCancelEdit = () => {
    if (editingField === 'location') {
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleSaveField = async (field) => {
    try {
      setSavingField(true);

      if (field === 'email') {
        await usersAPI.changeEmail(currentUser.id, editValue);
        const updatedUser = { ...currentUser, email: editValue, verified: false };
        setCurrentUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        onUserUpdate?.(updatedUser);
        setEditingField(null);
        setEditValue('');
        alert(t('profile.alerts.emailChanged'));
        return;
      }

      const value = (field === 'birthDate' && !editValue) ? null : editValue;
      const updatedUser = { ...currentUser, [field]: value };
      await usersAPI.update(currentUser.id, updatedUser);
      setCurrentUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      onUserUpdate?.(updatedUser);
      setEditingField(null);
      setEditValue('');
      if (field === 'displayName') {
        window.dispatchEvent(new Event('userUpdated'));
      }
    } catch (error) {
      console.error('Error saving field:', error);
      const msg = error.response?.data || 'Failed to save. Please try again.';
      alert(msg);
    } finally {
      setSavingField(false);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedPictureIndex === null) return;

      if (e.key === 'Escape') {
        handleCloseFullscreen();
      } else if (e.key === 'ArrowLeft') {
        if (selectedPictureIndex > 0) setSelectedPictureIndex(selectedPictureIndex - 1);
      } else if (e.key === 'ArrowRight') {
        if (selectedPictureIndex < userPictures.length - 1) setSelectedPictureIndex(selectedPictureIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedPictureIndex, userPictures.length]);

  if (!currentUser) {
    return (
      <div className="profile-page">
        <h2>{t('profile.title')}</h2>
        <div className="profile-content">
          <p>{t('profile.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">

      {/* Vertical Tab Control */}
      <div className="profile-tabs">
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile.tabs.myProfile')}
          </button>
          <button
            className={`tab-button ${activeTab === 'pictures' ? 'active' : ''}`}
            onClick={() => setActiveTab('pictures')}
          >
            {t('profile.tabs.myPictures')}
          </button>
          <button
            className={`tab-button ${activeTab === 'gatherings' ? 'active' : ''}`}
            onClick={() => setActiveTab('gatherings')}
          >
            {t('profile.tabs.pastGatherings')}
          </button>
          <button
            className={`tab-button ${activeTab === 'received' ? 'active' : ''}`}
            onClick={() => setActiveTab('received')}
          >
            {t('profile.tabs.receivedBadges', { count: receivedBadges.length })}
            {unreactedBadgeCount > 0 && (
              <span className="badge-notification-bubble">{unreactedBadgeCount}</span>
            )}
          </button>
          <button
            className={`tab-button ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            {t('profile.tabs.sentBadges', { count: sentBadges.length })}
          </button>
          <button
            className={`tab-button ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            {t('profile.tabs.friendsList')}
          </button>
          <button className="tab-button tab-button-logout" onClick={onLogout}>
            {t('profile.logout')}
          </button>
        </div>

        <div className="tab-content">
          {/* My Profile Tab */}
          {activeTab === 'profile' && (
            <div className="tab-panel">
              <div className="profile-info-box">
                <div className="avatar-and-info">
                  <div
                    className="avatar-display clickable-avatar-display"
                    onClick={() => setShowAvatarPreferenceModal(true)}
                    title={t('profile.avatar.clickToChange')}
                  >
                    <UserAvatar
                      user={{
                        ...currentUser,
                        publicActivityArtCount,
                        publicActivityArtLastMonth,
                        publicActivitySportCount,
                        publicActivitySportLastMonth,
                        publicActivityEducationCount,
                        publicActivityEducationLastMonth
                      }}
                      size="large"
                    />
                    <h2 className="profile-name">{currentUser.displayName}</h2>
                    {!currentUser.verified && (
                      <span className="profile-not-verified-badge">{t('profile.notVerified')}</span>
                    )}
                  </div>

                  <div className="user-details">
                    <div className="editable-row"><strong>{t('profile.fields.username')}</strong> @{currentUser.username}</div>

                    {editingField === 'displayName' ? (
                      <div className="edit-field-row">
                        <strong>{t('profile.fields.name')}</strong>
                        <input
                          type="text"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="edit-field-input"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveField('displayName'); if (e.key === 'Escape') handleCancelEdit(); }}
                        />
                        <button onClick={() => handleSaveField('displayName')} disabled={savingField} className="save-edit-btn">✓</button>
                        <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.name')}</strong> {currentUser.displayName}
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('displayName', currentUser.displayName)} title={t('profile.editTitles.name')}>✏️</button>
                      </div>
                    )}

                    {editingField === 'email' ? (
                      <div className="edit-field-row">
                        <strong>{t('profile.fields.email')}</strong>
                        <input
                          type="email"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="edit-field-input"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveField('email'); if (e.key === 'Escape') handleCancelEdit(); }}
                        />
                        <button onClick={() => handleSaveField('email')} disabled={savingField} className="save-edit-btn">✓</button>
                        <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.email')}</strong> {currentUser.email}
                        {currentUser.verified ? (
                          <span className="email-verified-badge" title={t('profile.emailVerified')}>✓</span>
                        ) : (
                          <span className="email-not-verified-badge">{t('profile.notVerified')}</span>
                        )}
                        {!currentUser.verified && (
                          verificationSent ? (
                            <span className="verification-sent-msg">{t('profile.verificationSent')}</span>
                          ) : (
                            <button
                              className="resend-verification-btn"
                              onClick={handleResendVerification}
                              disabled={sendingVerification}
                            >
                              {sendingVerification ? t('common.loading') : t('profile.resendVerification')}
                            </button>
                          )
                        )}
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('email', currentUser.email)} title={t('profile.editTitles.email')}>✏️</button>
                      </div>
                    )}

                    {editingField === 'birthDate' ? (
                      <div className="edit-field-row">
                        <strong>{t('profile.fields.birthDate')}</strong>
                        <input
                          type="date"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="edit-field-input"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Escape') handleCancelEdit(); }}
                        />
                        <button onClick={() => handleSaveField('birthDate')} disabled={savingField} className="save-edit-btn">✓</button>
                        <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.birthDate')}</strong>{' '}
                        {currentUser.birthDate
                          ? `${new Date(currentUser.birthDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} (${t('profile.age', { age: Math.floor((new Date() - new Date(currentUser.birthDate)) / (365.25 * 24 * 60 * 60 * 1000)) })})`
                          : t('common.notSet')}
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('birthDate', currentUser.birthDate)} title={t('profile.editTitles.birthDate')}>✏️</button>
                      </div>
                    )}

                    {editingField === 'bio' ? (
                      <div className="edit-field-row edit-bio-row">
                        <strong>{t('profile.fields.bio')}</strong>
                        <textarea
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          className="edit-field-input edit-textarea"
                          autoFocus
                          rows={3}
                          onKeyDown={e => { if (e.key === 'Escape') handleCancelEdit(); }}
                        />
                        <button onClick={() => handleSaveField('bio')} disabled={savingField} className="save-edit-btn">✓</button>
                        <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.bio')}</strong> {currentUser.bio || t('common.notSet')}
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('bio', currentUser.bio)} title={t('profile.editTitles.bio')}>✏️</button>
                      </div>
                    )}

                    {editingField === 'location' ? (
                      <div className="edit-field-row edit-location-row">
                        <strong>{t('profile.fields.homeLocation')}</strong>
                        <div ref={mapRef} className="location-map-inline"></div>
                        {locationAddress && (
                          <p className="location-address-display">{locationAddress}</p>
                        )}
                        <div className="edit-location-actions">
                          <button onClick={handleSaveLocation} disabled={savingField} className="save-edit-btn">✓</button>
                          <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                        </div>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.homeLocation')}</strong> {locationAddress || (currentUser.homeLatitude ? `${currentUser.homeLatitude.toFixed(4)}, ${currentUser.homeLongitude.toFixed(4)}` : '(not set)')}
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('location', null)} title={t('profile.editTitles.location')}>✏️</button>
                      </div>
                    )}

                    {editingField === 'preferredRadius' ? (
                      <div className="edit-field-row">
                        <strong>{t('profile.fields.preferredRadius')}</strong>
                        <select value={editValue} onChange={e => setEditValue(e.target.value)} className="edit-radius-select">
                          <option value={5}>5 km</option>
                          <option value={10}>10 km</option>
                          <option value={20}>20 km</option>
                          <option value={50}>50 km</option>
                        </select>
                        <button onClick={handleSaveRadius} disabled={savingField} className="save-edit-btn">✓</button>
                        <button onClick={handleCancelEdit} disabled={savingField} className="cancel-edit-btn">✕</button>
                      </div>
                    ) : (
                      <div className="editable-row">
                        <strong>{t('profile.fields.preferredRadius')}</strong> {currentUser.preferredRadiusKm || 10} km
                        <button className="edit-icon-btn" onClick={() => handleStartEdit('preferredRadius', currentUser.preferredRadiusKm || 10)} title={t('profile.editTitles.radius')}>✏️</button>
                      </div>
                    )}

                    <div className="editable-row">
                      <strong>{t('profile.fields.giveawayStars')}</strong>{' '}
                      {loadingStars ? (
                        <span className="loading-text">{t('profile.loadingStars')}</span>
                      ) : (
                        <span className="stars-count">⭐ {giveawayStars}</span>
                      )}
                      <button
                        className="store-link-btn"
                        onClick={() => navigate('/store')}
                      >
                        🛒 {t('profile.getMoreBadges')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Event Interests - Full Width Row */}
                <div className="event-interests-row">
                  <div className="hashtag-stats">
                    <h3>{t('profile.eventInterests')}</h3>
                    {loadingHashtags ? (
                      <p className="loading-text">{t('profile.loadingHashtags')}</p>
                    ) : hashtags.length === 0 ? (
                      <p className="no-hashtags">{t('profile.noEventsAttended')}</p>
                    ) : (
                      <div className="hashtag-cloud">
                        {hashtags.map(({ hashtag, count }) => (
                          <div key={hashtag} className="hashtag-stat">
                            <span className="hashtag-name">#{hashtag}</span>
                            <span className="hashtag-count">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="danger-zone">
                  <button
                    className="delete-account-btn"
                    onClick={() => setShowDeleteAccountModal(true)}
                  >
                    {t('profile.deleteAccount')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account Confirmation Modal */}
          {showDeleteAccountModal && (
            <div className="modal-overlay" onClick={() => !deletingAccount && setShowDeleteAccountModal(false)}>
              <div className="modal-content delete-account-modal" onClick={e => e.stopPropagation()}>
                <h3>{t('profile.deleteModal.title')}</h3>
                <p>{t('profile.deleteModal.confirm')}</p>
                <ul className="delete-account-list">
                  <li>{t('profile.deleteModal.items.personalData')}</li>
                  <li>{t('profile.deleteModal.items.pictures')}</li>
                  <li>{t('profile.deleteModal.items.connections')}</li>
                  <li>{t('profile.deleteModal.items.events')}</li>
                </ul>
                <p className="delete-account-warning">{t('profile.deleteModal.warning')}</p>
                <div className="modal-buttons">
                  <button
                    className="cancel-btn"
                    onClick={() => setShowDeleteAccountModal(false)}
                    disabled={deletingAccount}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="confirm-delete-btn"
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                  >
                    {deletingAccount ? t('profile.deleteModal.deletingBtn') : t('profile.deleteModal.confirmBtn')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* My Pictures Tab */}
          {activeTab === 'pictures' && (
            <div className="tab-panel">
              <div className="profile-gallery-full">
                <h3
                  className="gallery-title-clickable"
                  onClick={() => userPictures.length > 0 && setShowGalleryModal(true)}
                  title={userPictures.length > 0 ? t('profile.gallery.clickToView') : ""}
                >
                  {t('profile.gallery.title', { count: userPictures.length })}
                </h3>
                {loadingPictures ? (
                  <p className="loading-text">{t('profile.gallery.loading')}</p>
                ) : userPictures.length === 0 ? (
                  <p className="no-pictures">{t('profile.gallery.noPictures')}</p>
                ) : (
                  <div className="pictures-management-grid">
                    {userPictures.map((picture, index) => (
                      <div
                        key={picture.id}
                        className={`picture-card ${!picture.approved ? 'unapproved' : ''}`}
                      >
                        <div className="picture-image-container" onClick={() => setSelectedPictureIndex(index)}>
                          <img
                            src={picturesAPI.getImage(picture.picSysName, picture.connectedToUsername)}
                            alt={picture.picUserName}
                            className="picture-thumbnail"
                          />
                          {!picture.approved && (
                            <div className="pending-badge">{t('profile.gallery.pendingApproval')}</div>
                          )}
                        </div>
                        <div className="picture-info">
                          <p className="picture-name">{picture.picUserName}</p>
                          <p className="picture-event">{picture.eventName || picture.communityEventName || 'Event'}</p>
                          {(picture.eventDateTime || picture.communityEventDateTime) && (
                            <p className="picture-date">
                              {new Date(picture.eventDateTime || picture.communityEventDateTime).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        <div className="picture-controls">
                          <label className="approval-checkbox">
                            <input
                              type="checkbox"
                              checked={picture.approved}
                              onChange={() => handleApprovalToggle(picture.id, picture.approved)}
                            />
                            <span>{t('profile.gallery.approved')}</span>
                          </label>
                          <button
                            className="delete-picture-btn"
                            onClick={() => handleDeletePicture(picture.id)}
                            title="Delete picture"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Past Gatherings Tab */}
          {activeTab === 'gatherings' && (
            <div className="tab-panel">
              <div className="past-gatherings-section">
                
                {loadingPastGatherings ? (
                  <p className="loading-text">{t('profile.gatherings.loading')}</p>
                ) : pastGatherings.length === 0 ? (
                  <p className="no-gatherings">{t('profile.gatherings.none')}</p>
                ) : (
                  <div className="past-gatherings-grid">
                    {pastGatherings.map(event => (
                      <div key={event.id} className={`past-gathering-card ${getEventType(event.eventType)}`}>
                        <h4>{event.name}</h4>
                        <p>📅 {parseUTCDate(event.dateTime).toLocaleDateString('bg-BG')} at {parseUTCDate(event.dateTime).toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })}</p>
                        {event.address && <p>📍 {event.address}</p>}
                        {event.description && (
                          <p
                            className="gathering-description-preview"
                            onClick={() => setDescriptionPopup(event.description)}
                            title="Click to read full description"
                          >
                            {event.description}
                          </p>
                        )}
                        <div className="gathering-actions">
                          <button className="react-btn" onClick={() => handleShowParticipants(event)}>
                            {t('profile.gatherings.react')}
                          </button>
                          {event.organizer?.id === currentUser.id && (
                            <button
                              className="add-event-photo-btn"
                              onClick={() => handleAddGroupPhoto(event)}
                              disabled={!!groupPhotoExists[event.id]}
                            >
                              📷 {t('profile.gatherings.eventPhoto')}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Received Badges Tab */}
          {activeTab === 'received' && (
            <div className="tab-panel">
              <div className="received-badges-section-full">
                {loadingBadges ? (
                  <p className="loading-text">{t('profile.badges.loading')}</p>
                ) : receivedBadges.length === 0 ? (
                  <p className="no-badges">{t('profile.badges.none')}</p>
                ) : (
                  <div className="received-badges-grid">
                    {receivedBadges.map(badge => (
                      <div
                        key={badge.id}
                        className={`received-badge-item ${!badge.reaction ? 'clickable' : ''}`}
                        onClick={() => !badge.reaction && handleBadgeClick(badge)}
                        title={!badge.reaction ? t('profile.badges.clickToReact') : ''}
                      >
                        <div className="received-badge-icon">⭐</div>
                        <div className="received-badge-content">
                          <span className="received-badge-sender">
                            {badge.isAnonymous ? t('profile.badges.anonymous') : badge.givenByDisplayName}:
                          </span>
                          <span className="received-badge-message">
                            {badge.message || t('common.noMessage')}
                          </span>
                        </div>
                        {badge.reaction && (
                          <div className="received-badge-reaction">{badge.reaction}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sent Stars Tab */}
          {activeTab === 'sent' && (
            <div className="tab-panel">
              <div className="sent-stars-section">
                {loadingSentBadges ? (
                  <p className="loading-text">{t('profile.sentBadges.loading')}</p>
                ) : sentBadges.length === 0 ? (
                  <p className="no-sent-stars">{t('profile.sentBadges.none')}</p>
                ) : (
                  <div className="sent-stars-list">
                    {sentBadges.map(badge => (
                      <div key={badge.id} className="sent-star-item">
                        <div className="sent-star-icon">⭐</div>
                        <div className="sent-star-content">
                          <span className="sent-star-recipient">
                            {t('profile.sentBadges.to', { name: badge.recipientDisplayName })}
                          </span>
                          {badge.eventName && (
                            <span className="sent-star-event">
                              {t('profile.sentBadges.event', { name: badge.eventName })}
                            </span>
                          )}
                          {badge.message && (
                            <span className="sent-star-message">
                              "{badge.message}"
                            </span>
                          )}
                          {badge.isAnonymous && (
                            <span className="sent-star-anonymous">{t('profile.sentBadges.sentAnonymously')}</span>
                          )}
                        </div>
                        <div className="sent-star-reaction">
                          {badge.reaction ? (
                            <span className="reaction-received" title="Recipient's reaction">
                              {badge.reaction}
                            </span>
                          ) : (
                            <span className="reaction-waiting" title={t('profile.sentBadges.waitingReaction')}>
                              ...
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Friends List Tab */}
          {activeTab === 'friends' && (
            <div className="tab-panel">
              <div className="friends-list-section">
                <h3>{t('profile.friends.title')}</h3>
                {loadingEventCompanions ? (
                  <p className="loading-text">{t('profile.friends.loading')}</p>
                ) : eventCompanions.length === 0 ? (
                  <p className="no-companions">{t('profile.friends.none')}</p>
                ) : (
                  <div className="companions-list">
                    {eventCompanions.map(companion => (
                      <div key={companion.userId} className="companion-card">
                        <div className="companion-header">
                          <div className="companion-info">
                            <h4>
                              <button className="participant-name-btn" onClick={() => setSelectedUserId(companion.userId)}>
                                {companion.displayName}
                              </button>
                            </h4>
                            <span className="companion-username">@{companion.username}</span>
                          </div>
                          <div className="companion-stats">
                            <span className="shared-events-badge">
                              {t('profile.friends.event', { count: companion.sharedEventCount })}
                            </span>
                          </div>
                        </div>
                        <div className="companion-details">
                          <p><strong>{t('profile.friends.lastEvent')}</strong> {new Date(companion.lastEventDate).toLocaleDateString(i18n.language)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateEvent && !loading && (
        <div className="create-event-dialog-overlay" onClick={() => setShowCreateEvent(false)}>
          <div className="create-event-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="close-dialog" onClick={() => setShowCreateEvent(false)}>×</button>
            <CreateEventPanel
              currentUser={currentUser}
              allUsers={allUsers}
              onEventCreated={() => {
                setShowCreateEvent(false);
                navigate('/'); // Navigate to home page to see the new event
              }}
            />
          </div>
        </div>
      )}

      {showCreateCommunityEvent && !loading && (
        <div className="create-event-dialog-overlay" onClick={() => setShowCreateCommunityEvent(false)}>
          <div className="create-event-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="close-dialog" onClick={() => setShowCreateCommunityEvent(false)}>×</button>
            <CreateCommunityEventPanel
              currentUser={currentUser}
              onEventCreated={() => {
                setShowCreateCommunityEvent(false);
                navigate('/'); // Navigate to home page to see the new event
              }}
            />
          </div>
        </div>
      )}

      {showCreatePublicActivity && !loading && (
        <div className="create-event-dialog-overlay" onClick={() => setShowCreatePublicActivity(false)}>
          <div className="create-event-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="close-dialog" onClick={() => setShowCreatePublicActivity(false)}>×</button>
            <CreatePublicActivityPanel
              currentUser={currentUser}
              onEventCreated={() => {
                setShowCreatePublicActivity(false);
              }}
            />
          </div>
        </div>
      )}

      {selectedEvent && (
        <ReactDialog
          event={selectedEvent}
          currentUser={currentUser}
          giveawayStars={giveawayStars}
          givenStars={givenStars}
          existingPictures={existingPictures}
          onClose={handleCloseParticipants}
          onSendStar={handleSendStar}
          onAddPicture={handleAddPicture}
          onUserClick={(userId) => setSelectedUserId(userId)}
        />
      )}

      {showUploadModal && (selectedParticipant || groupPhotoEventId) && (
        <PictureUploadModal
          currentUser={currentUser}
          selectedParticipant={selectedParticipant}
          event={groupPhotoEventId ? groupPhotoEvent : selectedEvent}
          isGroupPhoto={!!groupPhotoEventId}
          onClose={handleCloseUploadModal}
          onUploaded={({ type, eventId, userId }) => {
            if (type === 'group') setGroupPhotoExists(prev => ({ ...prev, [eventId]: true }));
            else setExistingPictures(prev => ({ ...prev, [userId]: true }));
          }}
        />
      )}

      {selectedPictureIndex !== null && userPictures[selectedPictureIndex] && (
        <div className="fullscreen-viewer-overlay" onClick={handleCloseFullscreen}>
          <button className="fullscreen-close" onClick={handleCloseFullscreen}>×</button>

          {selectedPictureIndex > 0 && (
            <button className="fullscreen-nav fullscreen-prev" onClick={(e) => { e.stopPropagation(); handlePreviousPicture(); }}>
              ‹
            </button>
          )}

          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={picturesAPI.getImage(userPictures[selectedPictureIndex].picSysName, userPictures[selectedPictureIndex].connectedToUsername)}
              alt={userPictures[selectedPictureIndex].picUserName}
              className="fullscreen-image"
            />
            <div className="fullscreen-caption">
              <h3>{userPictures[selectedPictureIndex].picUserName}</h3>
              <p>{userPictures[selectedPictureIndex].eventName || 'Event'}</p>
              <p className="fullscreen-counter">
                {selectedPictureIndex + 1} / {userPictures.length}
              </p>
            </div>
          </div>

          {selectedPictureIndex < userPictures.length - 1 && (
            <button className="fullscreen-nav fullscreen-next" onClick={(e) => { e.stopPropagation(); handleNextPicture(); }}>
              ›
            </button>
          )}
        </div>
      )}

      {showStarModal && selectedStarRecipient && (
        <SendStarDialog
          recipient={selectedStarRecipient}
          giveawayStars={giveawayStars}
          onClose={handleCloseStarModal}
          onSubmit={handleSubmitStar}
        />
      )}

      {showGalleryModal && (
        <div className="gallery-modal-overlay" onClick={() => setShowGalleryModal(false)}>
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('profile.gallery.title', { count: userPictures.length })}</h4>
              <button className="close-dialog" onClick={() => setShowGalleryModal(false)}>×</button>
            </div>
            <div className="gallery-thumbnails">
              {userPictures.map((picture, index) => (
                <div
                  key={picture.id}
                  className="gallery-thumbnail"
                  onClick={() => {
                    setSelectedPictureIndex(index);
                    setShowGalleryModal(false);
                  }}
                >
                  <img
                    src={picturesAPI.getImage(picture.picSysName, picture.connectedToUsername)}
                    alt={picture.picUserName}
                    className="gallery-thumbnail-image"
                  />
                  <div className="gallery-thumbnail-info">
                    <p className="thumbnail-caption">{picture.picUserName}</p>
                    <p className="thumbnail-event">{picture.eventName || 'Event'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showAvatarPreferenceModal && (
        <div className="upload-picture-overlay" onClick={() => setShowAvatarPreferenceModal(false)}>
          <div className="avatar-preference-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('profile.avatar.chooseTitle')}</h4>
              <button className="close-dialog" onClick={() => setShowAvatarPreferenceModal(false)}>×</button>
            </div>

            <div className="avatar-preference-content">
              <div className="avatar-type-options">
                <div
                  className={`avatar-type-card ${currentUser.avatarType === AvatarType.PictureAndName ? 'selected' : ''}`}
                  onClick={() => {
                    if (currentUser.avatarType === AvatarType.PictureAndName) {
                      setShowAvatarUploadModal(true);
                    } else {
                      handleAvatarTypeChange(AvatarType.PictureAndName);
                    }
                  }}
                >
                  <UserAvatar user={{ ...currentUser, avatarType: AvatarType.PictureAndName }} size="medium" />
                  <h5>{t('profile.avatar.pictureAndName')}</h5>
                  <p className="avatar-type-description">{t('profile.avatar.pictureAndNameDesc')}</p>
                  {currentUser.avatarType === AvatarType.PictureAndName && (
                    <button className="upload-picture-button" onClick={(e) => { e.stopPropagation(); setShowAvatarPreferenceModal(false); setShowPictureSelectionModal(true); }}>
                      {t('profile.avatar.selectPicture')}
                    </button>
                  )}
                </div>

                <div
                  className={`avatar-type-card ${currentUser.avatarType === AvatarType.CommunityWork ? 'selected' : ''} ${currentUser.communityWorkCount === 0 ? 'disabled' : ''}`}
                  onClick={() => currentUser.communityWorkCount > 0 && handleAvatarTypeChange(AvatarType.CommunityWork)}
                >
                  <UserAvatar user={{ ...currentUser, avatarType: AvatarType.CommunityWork }} size="medium" />
                  <h5>{t('profile.avatar.communityWork')}</h5>
                  <p className="avatar-type-description">
                    {currentUser.communityWorkCount > 0
                      ? t('profile.avatar.totalLastMonth', { total: currentUser.communityWorkCount, lastMonth: lastMonthCount })
                      : t('profile.avatar.totalOnly', { total: currentUser.communityWorkCount })}
                  </p>
                  {currentUser.communityWorkCount === 0 && (
                    <p className="unlock-hint">{t('profile.avatar.unlockHint')}</p>
                  )}
                </div>

                {/* TODO: Future avatar types
                <div
                  className={`avatar-type-card ${currentUser.avatarType === 'public-art' ? 'selected' : ''} ${publicActivityArtCount === 0 ? 'disabled' : ''}`}
                  onClick={() => publicActivityArtCount > 0 && handleAvatarTypeChange('public-art')}
                >
                  <UserAvatar user={{ ...currentUser, avatarType: 'public-art', publicActivityArtCount, publicActivityArtLastMonth }} size="medium" />
                  <h5>Public Activity - Art</h5>
                  <p className="avatar-type-description">
                    {publicActivityArtCount} total
                    {publicActivityArtCount > 0 && `, ${publicActivityArtLastMonth} last month`}
                  </p>
                  {publicActivityArtCount === 0 && (
                    <p className="unlock-hint">Attend art/music events to unlock</p>
                  )}
                </div>

                <div
                  className={`avatar-type-card ${currentUser.avatarType === 'public-sport' ? 'selected' : ''} ${publicActivitySportCount === 0 ? 'disabled' : ''}`}
                  onClick={() => publicActivitySportCount > 0 && handleAvatarTypeChange('public-sport')}
                >
                  <UserAvatar user={{ ...currentUser, avatarType: 'public-sport', publicActivitySportCount, publicActivitySportLastMonth }} size="medium" />
                  <h5>Public Activity - Sport</h5>
                  <p className="avatar-type-description">
                    {publicActivitySportCount} total
                    {publicActivitySportCount > 0 && `, ${publicActivitySportLastMonth} last month`}
                  </p>
                  {publicActivitySportCount === 0 && (
                    <p className="unlock-hint">Attend sport events to unlock</p>
                  )}
                </div>

                <div
                  className={`avatar-type-card ${currentUser.avatarType === 'public-education' ? 'selected' : ''} ${publicActivityEducationCount === 0 ? 'disabled' : ''}`}
                  onClick={() => publicActivityEducationCount > 0 && handleAvatarTypeChange('public-education')}
                >
                  <UserAvatar user={{ ...currentUser, avatarType: 'public-education', publicActivityEducationCount, publicActivityEducationLastMonth }} size="medium" />
                  <h5>Public Activity - Education</h5>
                  <p className="avatar-type-description">
                    {publicActivityEducationCount} total
                    {publicActivityEducationCount > 0 && `, ${publicActivityEducationLastMonth} last month`}
                  </p>
                  {publicActivityEducationCount === 0 && (
                    <p className="unlock-hint">Attend education events to unlock</p>
                  )}
                </div>
                */}
              </div>

              {savingAvatarType && (
                <div className="saving-indicator">
                  <p>{t('profile.avatar.saving')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAvatarUploadModal && (
        <div className="upload-picture-overlay" onClick={() => setShowAvatarUploadModal(false)}>
          <div className="upload-picture-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('profile.uploadAvatar.title')}</h4>
              <button className="close-dialog" onClick={() => setShowAvatarUploadModal(false)}>×</button>
            </div>

            <div className="upload-form">
              <div className="form-group">
                <label>{t('profile.uploadAvatar.selectLabel')}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setAvatarPictureFile(file);
                    }
                  }}
                  className="file-input"
                />
                {avatarPictureFile && (
                  <p className="file-info">
                    {t('profile.uploadAvatar.selected', { name: avatarPictureFile.name, size: (avatarPictureFile.size / 1024).toFixed(2) })}
                  </p>
                )}
              </div>

              <div className="upload-actions">
                <button
                  className="cancel-upload-btn"
                  onClick={() => {
                    setShowAvatarUploadModal(false);
                    setAvatarPictureFile(null);
                  }}
                  disabled={uploadingAvatarPicture}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="upload-btn"
                  onClick={async () => {
                    if (!avatarPictureFile) {
                      alert(t('profile.alerts.selectImage'));
                      return;
                    }

                    try {
                      setUploadingAvatarPicture(true);
                      alert(t('profile.alerts.uploadSoon'));
                      // TODO: Implement avatar picture upload to backend
                      setShowAvatarUploadModal(false);
                      setAvatarPictureFile(null);
                    } catch (error) {
                      console.error('Error uploading avatar picture:', error);
                      alert(t('profile.alerts.failedUploadAvatar'));
                    } finally {
                      setUploadingAvatarPicture(false);
                    }
                  }}
                  disabled={uploadingAvatarPicture || !avatarPictureFile}
                >
                  {uploadingAvatarPicture ? t('profile.uploadAvatar.uploading') : t('profile.uploadAvatar.upload')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBadgeReactionModal && selectedBadge && (
        <div className="upload-picture-overlay" onClick={handleCloseBadgeReactionModal}>
          <div className="badge-reaction-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('profile.badgeReaction.title')}</h4>
              <button className="close-dialog" onClick={handleCloseBadgeReactionModal}>×</button>
            </div>

            <div className="badge-reaction-content">
              {!selectedBadge.isAnonymous && (
                <div className="badge-sender-info">
                  <p className="badge-from">
                    <strong>{t('profile.badgeReaction.from')}</strong> {selectedBadge.givenByDisplayName}
                  </p>
                  {selectedBadge.eventName && (
                    <p className="badge-event">
                      <strong>{t('profile.badgeReaction.event')}</strong> {selectedBadge.eventName}
                    </p>
                  )}
                </div>
              )}

              <div className="badge-message-section">
                <p className="badge-message-label">{t('profile.badgeReaction.message')}</p>
                <p className="badge-message-text">
                  {selectedBadge.message || t('common.noMessage')}
                </p>
              </div>

              <div className="badge-reactions-section">
                <p className="reactions-label">{t('profile.badgeReaction.chooseReaction')}</p>
                <div className="reaction-buttons">
                  {['❤️', '😊', '🙏', '😕', '😢'].map(emoji => (
                    <button
                      key={emoji}
                      className={`reaction-btn ${selectedReaction === emoji ? 'selected' : ''}`}
                      onClick={() => setSelectedReaction(emoji)}
                      disabled={settingReaction}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div className="badge-offended-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={badgeOffended}
                    onChange={(e) => setBadgeOffended(e.target.checked)}
                    className="offended-checkbox"
                  />
                  <span className="checkbox-text offended-text">{t('profile.badgeReaction.offended')}</span>
                </label>
              </div>

              <div className="badge-reaction-actions">
                <button
                  className="upload-btn react-submit-btn"
                  onClick={handleSubmitReaction}
                  disabled={settingReaction || !selectedReaction}
                >
                  {settingReaction ? t('profile.badgeReaction.sending') : t('profile.badgeReaction.react')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPictureSelectionModal && (
        <div className="picture-selection-overlay" onClick={() => setShowPictureSelectionModal(false)}>
          <div className="picture-selection-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h4>{t('profile.pictureSelection.title')}</h4>
              <button className="close-dialog" onClick={() => setShowPictureSelectionModal(false)}>×</button>
            </div>

            <div className="picture-selection-content">
              {userPictures.length === 0 ? (
                <p className="no-pictures-message">
                  {t('profile.pictureSelection.noPictures')}
                </p>
              ) : (
                <div className="picture-selection-grid">
                  {userPictures.map((picture) => (
                    <div
                      key={picture.id}
                      className="picture-selection-item"
                      onClick={() => handleAvatarPictureSelected(picture.id)}
                    >
                      <img
                        src={picturesAPI.getImage(picture.picSysName, currentUser.username)}
                        alt={picture.picUserName}
                      />
                      <p className="picture-name">{picture.picUserName}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="picture-selection-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowPictureSelectionModal(false)}
                >
                  {t('common.cancel')}
                </button>
                {currentUser.avatarPictureId && (
                  <button
                    className="clear-avatar-btn"
                    onClick={handleClearAvatarPicture}
                  >
                    {t('profile.pictureSelection.clearSelection')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedUserId && createPortal(
        <UserDetail userId={selectedUserId} onClose={() => setSelectedUserId(null)} />,
        document.body
      )}

      {descriptionPopup && (
        <div className="description-popup-overlay" onClick={() => setDescriptionPopup(null)}>
          <div className="description-popup-box" onClick={e => e.stopPropagation()}>
            <button className="description-popup-close" onClick={() => setDescriptionPopup(null)}>×</button>
            <p>{descriptionPopup}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
